import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import AppStoreBadges from './AppStoreBadges';

const PAGE_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'For Developers', href: '/for-developers' },
  { label: 'Community', href: '/community' },
];

const SOCIAL = [
  { label: 'Discord', href: 'https://discord.com/invite/mN8ENbzEMJ' },
  { label: 'Instagram', href: 'https://www.instagram.com/funizzle/' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/funizzle/' },
];

const LEGAL = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Request Account Deletion', href: '/delete-account' },
];

const linkStyle = {
  color: '#fff',
  fontFamily: 'Poppins',
  fontSize: { xs: 13, md: 14 },
  textDecoration: 'none',
  opacity: 0.85,
  '&:hover': { opacity: 1, textDecoration: 'underline' },
};

export default function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        zIndex: 30,
        backgroundColor: 'rgba(8, 8, 16, 0.92)',
        backdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        color: '#fff',
        py: { xs: 5, md: 7 },
        px: { xs: 3, sm: 5, md: 8 },
      }}
    >
      {/* Top row: Brand + CTA */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
          gap: { xs: 4, md: 5 },
          alignItems: 'center',
          maxWidth: 1200,
          mx: 'auto',
          mb: { xs: 4, md: 5 },
          pb: { xs: 4, md: 5 },
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/Logo1.svg"
            alt="BingePlay"
            sx={{ width: { xs: 56, md: 72 } }}
          />
          <Box>
            <Typography
              sx={{
                fontFamily: 'Rammetto One',
                textTransform: 'uppercase',
                fontSize: { xs: 16, md: 20 },
                lineHeight: 1.1,
              }}
            >
              Get the app
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: { xs: 12, md: 14 },
                color: 'rgba(255,255,255,0.7)',
                mt: 0.5,
              }}
            >
              Swipe through playable game demos in your feed.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <AppStoreBadges scale={0.95} />
        </Box>
      </Box>

      {/* Link columns */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 3, md: 4 },
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        <FooterColumn title="BingePlay" items={PAGE_LINKS} />
        <FooterColumn title="Community" items={SOCIAL} external />
        <FooterColumn title="Legal" items={LEGAL} />
        <Box>
          <Typography
            sx={{
              fontFamily: 'Rammetto One',
              textTransform: 'uppercase',
              fontSize: { xs: 13, md: 14 },
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.55)',
              mb: 1.5,
            }}
          >
            Get in touch
          </Typography>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: { xs: 13, md: 14 }, color: '#fff' }}>
            support@funizzle.com
          </Typography>
        </Box>
      </Box>

      {/* Bottom */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          mt: { xs: 4, md: 6 },
          pt: 3,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontSize: { xs: 12, md: 13 },
            color: 'rgba(255,255,255,0.55)',
          }}
        >
          © {new Date().getFullYear()} Funizzle, Inc. BingePlay is a Funizzle product.
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontSize: { xs: 12, md: 13 },
            color: 'rgba(255,255,255,0.55)',
            letterSpacing: '0.04em',
          }}
        >
          The TikTok for games.
        </Typography>
      </Box>
    </Box>
  );
}

function FooterColumn({ title, items, external }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: 'Rammetto One',
          textTransform: 'uppercase',
          fontSize: { xs: 13, md: 14 },
          letterSpacing: '0.08em',
          color: 'rgba(255,255,255,0.55)',
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item) => (
          <Box
            key={item.href}
            component="a"
            href={item.href}
            {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
            sx={linkStyle}
          >
            {item.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

FooterColumn.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, href: PropTypes.string }))
    .isRequired,
  external: PropTypes.bool,
};
